import { getProvider } from '../svpProvider';
import { getLogger } from '../logger';
import { buildComponentWritePayload } from '../../components/ComponentForm/buildComponentWritePayload';

import { isSuccessResponse, requestAPI } from './shared/requestAPI';

import type { ComponentResponse, ComponentInput } from '../../types';

const logger = getLogger('componentsApi');

export async function createComponent(
  componentDraft: Partial<ComponentInput & { id: string }>,
  { pageId, index }: { pageId?: string; index?: number } = {}
) {
  // C1/C2/C-N/C-F/C-cat: apply the allow-list and per-type sanitization
  const fields = buildComponentWritePayload(
    componentDraft as Record<string, unknown>
  );

  // C3: transport fields are added to the body AFTER the allow-list so they
  // never pollute the component schema validation path.
  const body: Record<string, unknown> = { ...fields };
  if (pageId !== undefined) body.pageId = pageId;
  if (index !== undefined) body.index = index;

  try {
    const result = await requestAPI<ComponentResponse>(
      `/${getProvider()}/components`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }
    );
    if (isSuccessResponse(result)) {
      return result!;
    }
    throw result;
  } catch (error) {
    logger.error('Failed to create component', { fields });
    throw error;
  }
}

export async function updateComponent(
  componentId: ComponentResponse['id'],
  data: Partial<ComponentInput>
) {
  // C1/C2/C-N/C-F/C-cat: apply the allow-list and per-type sanitization
  const fields = buildComponentWritePayload(data as Record<string, unknown>);
  try {
    const result = await requestAPI<ComponentResponse>(
      `/${getProvider()}/components/${componentId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(fields),
        headers: { 'Content-Type': 'application/json' },
      }
    );
    if (isSuccessResponse(result)) {
      return result!;
    }
    throw result;
  } catch (error) {
    logger.error('Failed to update component', {
      componentId,
      fields,
    });
    throw error;
  }
}

export async function fetchComponent(componentId: string) {
  try {
    const result = await requestAPI<ComponentResponse>(
      `/${getProvider()}/components/${componentId}`
    );
    if (isSuccessResponse(result)) {
      return result!;
    }
    throw result;
  } catch (error) {
    logger.error('Failed to fetch component', { componentId });
    throw error;
  }
}

export async function fetchSharedComponents() {
  try {
    const result = await requestAPI<ComponentResponse[]>(
      `/${getProvider()}/components/shared`
    );
    if (isSuccessResponse(result)) {
      return result!;
    }
    throw result;
  } catch (error) {
    logger.error('Failed to fetch shared components');
    throw error;
  }
}
